import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { vehicleApi } from "@/services/api";
import { AppHeader } from "@/components/layout/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Search,
  Eye,
  Trash2,
  Car,
  CheckCircle2,
  Clock,
  Wrench,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  Fuel,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";

const statusConfig = {
  available: {
    label: "Available",
    className: "bg-success-light text-success border-success/20",
    icon: CheckCircle2
  },
  rented: {
    label: "Rented",
    className: "bg-primary/10 text-primary border-primary/20",
    icon: Clock
  },
  maintenance: {
    label: "Maintenance",
    className: "bg-warning-light text-warning border-warning/20",
    icon: Wrench
  }
};

const categoryLabels = {
  sedan: "Sedan",
  suv: "SUV",
  truck: "Truck",
  luxury: "Luxury",
  electric: "Electric",
  van: "Van"
};

export default function VehiclesListPage() {
  const navigate = useNavigate();
  
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState({ total: 0, available: 0, rented: 0, maintenance: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [pageSize] = useState(10);

  const fetchVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await vehicleApi.getVehicles(currentPage, pageSize, searchQuery, statusFilter, categoryFilter);
      setVehicles(data.vehicles);
      setTotalPages(data.totalPages);
      setTotalVehicles(data.total);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error("Failed to load vehicles");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, statusFilter, categoryFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await vehicleApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const seedDatabase = async () => {
    try {
      const result = await vehicleApi.seedVehicles();
      toast.success("Database Seeded", { description: result.message });
      fetchVehicles();
      fetchStats();
    } catch (error) {
      console.error('Error seeding database:', error);
    }
  };

  useEffect(() => {
    fetchVehicles();
    fetchStats();
  }, [fetchVehicles, fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchVehicles();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, categoryFilter]);

  const handleCreateVehicle = async () => {
    try {
      const newVehicle = await vehicleApi.createVehicle();
      toast.success("Vehicle Created", {
        description: `New vehicle ${newVehicle.id} has been created.`
      });
      navigate(`/vehicle/${newVehicle.id}?edit=true`);
    } catch (error) {
      toast.error("Failed to create vehicle");
    }
  };

  const handleDeleteVehicle = async (id) => {
    try {
      await vehicleApi.deleteVehicle(id);
      toast.success("Vehicle Deleted", {
        description: `Vehicle ${id} has been deleted.`
      });
      fetchVehicles();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete vehicle");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Fleet Vehicles</h1>
            <p className="text-muted-foreground mt-1">Manage your rental vehicle inventory</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={seedDatabase} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Seed Data
            </Button>
            <Button onClick={handleCreateVehicle} className="gap-2">
              <Plus className="w-4 h-4" />
              Add Vehicle
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Car className="w-4 h-4" />
                Total Fleet
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-success" />
                Available
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-success">{stats.available}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Rented
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-primary">{stats.rented}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-warning" />
                Maintenance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-warning">{stats.maintenance}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by make, model, license plate, or VIN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="rented">Rented</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="sedan">Sedan</SelectItem>
                  <SelectItem value="suv">SUV</SelectItem>
                  <SelectItem value="truck">Truck</SelectItem>
                  <SelectItem value="luxury">Luxury</SelectItem>
                  <SelectItem value="electric">Electric</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Vehicles Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Vehicles</CardTitle>
            <CardDescription>
              {totalVehicles} vehicle{totalVehicles !== 1 ? 's' : ''} found
              {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="font-semibold">Vehicle</TableHead>
                    <TableHead className="font-semibold hidden md:table-cell">Category</TableHead>
                    <TableHead className="font-semibold hidden lg:table-cell">License Plate</TableHead>
                    <TableHead className="font-semibold">Daily Rate</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                          <p className="text-muted-foreground">Loading vehicles...</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : vehicles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3 text-muted-foreground">
                          <Car className="w-12 h-12 opacity-30" />
                          <p>No vehicles found</p>
                          <Button variant="outline" size="sm" onClick={handleCreateVehicle}>
                            Add your first vehicle
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    vehicles.map((vehicle, index) => {
                      const StatusIcon = statusConfig[vehicle.status]?.icon || CheckCircle2;
                      
                      return (
                        <TableRow
                          key={vehicle.id}
                          className={`cursor-pointer transition-colors ${index % 2 === 1 ? 'bg-muted/30' : ''}`}
                          onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted">
                                <Car className="w-5 h-5 text-muted-foreground" />
                              </div>
                              <div>
                                <p className="font-medium text-foreground">
                                  {vehicle.year} {vehicle.make} {vehicle.model}
                                </p>
                                <p className="text-xs text-muted-foreground">{vehicle.color} • {vehicle.id}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge variant="outline" className="font-normal">
                              {categoryLabels[vehicle.category] || vehicle.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell font-mono text-sm">
                            {vehicle.licensePlate}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {formatCurrency(vehicle.dailyRate)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`gap-1 ${statusConfig[vehicle.status]?.className || ''}`}
                            >
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig[vehicle.status]?.label || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete this {vehicle.year} {vehicle.make} {vehicle.model}? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteVehicle(vehicle.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalVehicles)} of {totalVehicles} vehicles
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className="w-8 h-8 p-0"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
